import React from 'react';

import ReactECharts from 'echarts-for-react';

import {

  INTERNAL_SE_SOFT_BLUE_COLORS,

  buildChartTheme,

  truncateChartLabel,

} from './chartTheme';

import useLaptopCompact from './useLaptopCompact';

import ChartEmptyState from './ChartEmptyState';

import DonutChartFrame from './DonutChartFrame';



const META_KEYS = new Set(['total', 'totalRuns', 'totalCount', '__total', '__center']);



const formatPercentage = (value) => {

  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) return '0%';



  const rounded = Math.round(numericValue * 100) / 100;

  if (Number.isInteger(rounded)) return `${rounded}%`;

  return `${rounded.toFixed(2)}%`;

};



const formatWholeNumber = (value) => {

  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) return '0';

  return Math.round(numericValue).toString();

};



const PieChart = ({

  data,

  donut = false,

  renderZeroWhenEmpty = false,

  zeroRegions = ['APJ', 'EMEA', 'NA', 'LATAM'],

  emptyStateTitle = 'No activity for this period',

  emptyStateSubtitle = 'Try changing the time filter.',

  emptyStateCompact = false,

}) => {

  const isLaptopCompact = useLaptopCompact();

  const theme = buildChartTheme(isLaptopCompact);



  const hasData = !!(data && Object.keys(data).length > 0);

  if (!hasData && !renderZeroWhenEmpty) {

    return (

      <ChartEmptyState

        title={emptyStateTitle}

        subtitle={emptyStateSubtitle}

        compact={emptyStateCompact}

      />

    );

  }



  const normalizedData = hasData

    ? data

    : zeroRegions.reduce((acc, region) => {

        acc[region] = 0;

        return acc;

      }, {});



  const chartEntries = Object.entries(normalizedData).filter(([key]) => !META_KEYS.has(key));

  const formattedData = chartEntries.map(([key, rawValue], idx) => ({

    name: key,

    value: Number(rawValue) || 0,

    itemStyle: { color: INTERNAL_SE_SOFT_BLUE_COLORS[idx % INTERNAL_SE_SOFT_BLUE_COLORS.length] },

  }));



  const totalValue = formattedData.reduce((sum, item) => sum + item.value, 0);

  const preferredCenterCount = Number(

    normalizedData?.totalRuns

      ?? normalizedData?.totalCount

      ?? normalizedData?.__total

      ?? normalizedData?.__center

  );

  const centerText = Number.isFinite(preferredCenterCount)

    ? formatWholeNumber(preferredCenterCount)

    : formatPercentage(totalValue);



  const donutCenter = ['38%', '50%'];

  const chartHeight = donut ? (isLaptopCompact ? 282 : 300) : (isLaptopCompact ? 320 : 340);



  const options = {

    animation: false,

    tooltip: {

      ...theme.tooltipBase,

      trigger: 'item',

      formatter: (params) => `${params?.name || ''}: ${formatPercentage(params?.value)}`,

    },

    legend: {

      ...theme.legendBase,

      orient: 'vertical',

      right: isLaptopCompact ? 4 : 8,

      top: 'middle',

      formatter: (name) => {

        const item = formattedData.find((d) => d.name === name);

        return item ? `${truncateChartLabel(name, 18)}  ${formatPercentage(item.value)}` : truncateChartLabel(name, 18);

      },

    },

    series: [

      {

        type: 'pie',

        radius: donut ? ['42%', '68%'] : ['0%', '68%'],

        center: donut ? donutCenter : ['50%', '50%'],

        data: formattedData,

        label: { show: false },

        labelLine: { show: false },

        emphasis: {

          itemStyle: { shadowBlur: 8, shadowColor: 'rgba(0,0,0,0.12)' },

        },

      },

    ],

  };



  const chart = (

    <ReactECharts

      option={options}

      style={{ height: `${chartHeight}px`, width: '100%' }}

      opts={{ renderer: 'canvas' }}

    />

  );



  if (!donut) {

    return chart;

  }



  return (

    <DonutChartFrame

      center={donutCenter}

      centerLabel={centerText}

      centerStyle={{ fontSize: theme.fs(16) }}

      height={`${chartHeight}px`}

    >

      {chart}

    </DonutChartFrame>

  );

};



export default PieChart;

